export type MaybeLeaderboardResult = {
  name: string;
  value: number | null;
};
export type LeaderboardResult = {
  name: string;
  value: number;
  latest_book: string | null;
  book_cover: string | null;
  book_cover_thumbnail: string | null;
  profile_id: number;
  profile_image: string | null;
  profile_image_thumbnail: string | null;
};

export enum ReactionType {
  Think = '🤔',
  Laugh = '😂',
  Fire = '🔥',
  Heart = '❤',
  Like = '👍',
}

export type Reaction = {
  value: ReactionType;
  reaction_giver: BookwiseUser;
  id: string;
};

export enum FeedbackType {
  Kudos = 'kudos',
  Comment = 'comment'
}


export type Feedback = {
  id: string;
  feedback_giver: BookwiseUser;
  feedback_type: FeedbackType;
  comment?: string;
  image?: string;
  created: number;
  reactions: Reaction[];
  rating?: number;
  is_auto_generated?: boolean;
};

type FeedResultDeviceInfo = {'model': string; 'type': string; 'vendor': string;};

export enum ReadingSessionOrigin {
  Bookwise = 'bookwise',
  Kindle = 'kindle',
  Manual = 'manual',
}

export type ReadingSession = {
  'ended_at': number | null;
  'percent_at_end': number;
  'percent_at_start': number;
  'page_at_start'?: number | null;
  'page_at_end'?: number | null;
  'published_at': number;
  'started_at': number;
  'still_ongoing': boolean;
  'summary': string | null;
  'total_time_seconds': number;
  'total_xp': number;
  'id': number;
  'bookwise_profile': BookwiseUser;
  'tracked_book': TrackedBook;
  'is_tts_session': boolean | null;
  'device_info': FeedResultDeviceInfo | { [key: string]: never; };
  'origin': ReadingSessionOrigin;
};

export type ReadingSessionsForTrackedBookAggregates = {
  total_words: number;
  total_time_seconds: number;
  avg_wpm: number;
  progress: number | null;
  longest_session_seconds: number;
};

export type DailyProgress = {
  date: string;
  percent_start: number;
  percent_end: number;
};

export type ReadingProgressForTrackedBookResponse = {
  session_count: number;
  aggregates: ReadingSessionsForTrackedBookAggregates;
  daily_progress: DailyProgress[];
};

export type NestedReadingSession = {
  'id': number;
  'owner': BookwiseUser;
  'book_title': string;
  'published_at': number;
  'created': number;
};

export type NestedBookReview = {
  'id': number;
  'owner': BookwiseUser;
  'book_title': string;
  'rating'?: number;
  'user_description'?: string;
  'created': number;
  'image'?: string;
};

export type NestedBookStart = {
  'id': number;
  'owner': BookwiseUser;
  'book_title': string;
  'started_at': number;
  'user_description'?: string;
  'created': number;
  'image'?: string;
};

export type NestedFeedPost = {
  post_type: FeedPostType;
  id: number;
  owner: BookwiseUser;
  created: number;
  reading_session?: NestedReadingSession;
  book_review?: NestedBookReview;
  book_start?: NestedBookStart;
};

export type Follow = {
  id: number;
  follower: BookwiseUser;
  following: BookwiseUser;
  created: string;
};

export enum NotificationType {
  FeedbackOnOwnSession = 'feedback_on_own_session',
  FeedbackOnOthersSession = 'feedback_on_others_session',
  FollowedByUser = 'followed_by_user',
  AtMentionInComment = 'at_mention_in_comment',
  AtMentionInPostDescription = 'at_mention_in_post_description',
  ReactionToOwnComment = 'reaction_on_own_comment',
  SessionPublished = 'session_published',
  PodiumPlacement = 'podium_placement',
  FollowerFinishedBook = 'follower_finished_book',
  Custom = 'custom',
}

export type NotificationItem = {
  created: string;
  id: number;
  notification_type: NotificationType;
  is_direct: boolean;
  receiver: BookwiseUser;
  post_feedback?: Feedback;
  feed_post?: NestedFeedPost;
  follow?: Follow;
  reaction?: Reaction;
  seen: boolean;
  podium_position?: number;
  body?: string;
  title?: string;
  icon_url?: string;
  deep_link?: string;
};

export type SessionNotification = {
  post_id?: number;
  feedback_id?: string;
  notification_id?: number;
  podium_position?: number;
  deep_link?: string;
};

export type BookwiseProfileLink = {
  id: string;
  link: string;
  title: string;
};

export enum BookwiseProfileBackgroundKind {
  None = 'none',
  CustomImage = 'custom_image',
  PresetGreen = 'preset_green',
  PresetPurple = 'preset_purple',
  PresetRed = 'preset_red',
}

export type BookwiseUser = {
  profile_id: number;
  is_following: boolean;
  is_following_you: boolean;
  is_self: boolean;
  is_staff: boolean;
  profile_image: string | null;
  profile_image_thumbnail: string | null;
  profile_background_image: string | null;
  profile_background_image_thumbnail: string | null;
  profile_background_kind: BookwiseProfileBackgroundKind;
  username: string;
  first_name: string;
  last_name: string | null;
  joined_at: number | null;
  description: string;
  links: BookwiseProfileLink[];
  location: string;
};

export type BookwiseUserWihPodium = BookwiseUser & {
  podium_badge?: number;
};

export type BookwiseUserWithLatestBook = BookwiseUser & {
  latest_book?: string | null;
  book_cover?: string | null;
  book_cover_thumbnail?: string | null;
};

export enum TrackedBookStatus {
  Unsorted = 'unsorted',
  WantToRead = 'want_to_read',
  CurrentlyReading = 'currently_reading',
  Finished = 'finished',
  DidNotFinish = 'did_not_finish',
  Deleted = 'deleted',
}

export enum TrackedBookOrigin {
  BookstoreEbook = 'bookstore_ebook',
  UploadedEbook = 'uploaded_ebook',
  Physical = 'physical',
  Kindle = 'kindle',
  Goodreads = 'goodreads',
  Bookwise = 'bookwise',
}

// NOTE: these are independent. E.g. `ghostreader` does not override any of the others.
export type BookstoreProductFeatures = {
  recap_summaries: boolean;
  ghostreader: boolean; // Legacy AI features / everything else other than the other properties here
  word_lookup: boolean; // i.e. document grounded lookup (the lookup tab in the Quick Lookup sheet)
};

export type TrackedBook = {
  id: string;
  created: number;
  updated: number;
  title: string | null;
  author: string | null;
  cover_url: string | null;
  cover_thumbnail_url: string | null;
  isbn: string | null;
  asin: string | null;
  is_sample: boolean | null;
  progress: number | null;
  creation_origin?: TrackedBookOrigin;
  goodreads_book_id: string | null;
  kindle_user_book_id: number | null;
  status: TrackedBookStatus;
  description: string | null;
  // stripped html text of description
  description_text: string | null;
  short_description: string | null;
  publisher_description_is_low_quality: boolean;
  genre_classification: string[] | null;
  published_at: string | null;

  catalog_product_id: string | null;
  document_id: string | null;
  bookwise_profile_id: number | null;
  last_read_at: number | null;
  started_reading_at: number | null;
  finished_reading_at: number | null;
  status_changed_at: number | null;
  page_count: number | null;
  manual_total_pages: number | null;
  word_count: number | null;
  reading_time: string | null;

  features: BookstoreProductFeatures;
  session_count: number;
};

export type TrendingBook = TrackedBook & {
  rank: number;
  reader_count: number;
};

export type DailyStats = {
   'number_of_highlights': number;
   'total_time_in_seconds': number;
   'total_xp': number;
};

export type StatsSession = {
  id: number;
  tracked_book_id: number;
  started_at: number;
  ended_at: number | null;
  // Calendar day in the profile's timezone, attributed server-side — the
  // client never derives days from timestamps.
  local_date: string;
  words: number | null;
  time_seconds: number | null;
  percent_at_start?: number | null;
  percent_at_end: number | null;
};

export type StatsBook = {
  tracked_book_id: number;
  title: string | null;
  author: string | null;
  cover_url: string | null;
  cover_thumbnail_url?: string | null;
  user_rating: number | null;
  status: TrackedBookStatus;
  started_reading_at: number | null;
  finished_reading_at: number | null;
  // Calendar day in the profile's timezone, attributed server-side — the
  // client never derives days from timestamps.
  finished_local_date: string | null;
  percent_complete_at_period_end: number | null;
  words_read_in_period: number;
  seconds_spent_in_period: number;
  sessions_count_in_period: number;
};

export type WeeklyReadingRecapBook = {
  tracked_book: TrackedBook;
  total_words: number;
  progress: number | null;
  rating: number | null;
};

export type WeeklyReadingRecapWeekData = {
  profile_id: number;
  week_start: string;
  week_end: string;
  display_start_date: string;
  display_end_date: string;
  sessions: ReadingSession[];
  snapshot_books: WeeklyReadingRecapBook[];
};

export type WeeklyReadingRecapWeek = {
  results: DailyStats[];
  snapshot_books?: WeeklyReadingRecapBook[];
  week_start: string;
  week_end: string;
  display_start_date: string;
  display_end_date: string;
  previous_snapshot_week_start: string | null;
  latest_snapshot_week_start: string | null;
  snapshot_best_session: ReadingSession | null;
};

export type YearlyDailyStats = {
  date: string;
  words: number;
  time_seconds: number;
};

export type YearlyGoal = {
  id: number;
  year: number;
  target_books_read: number;
};

export type BookstoreProductFormat =
  | 'cd_audio'
  | 'audiobook'
  | 'ebook'
  | 'hardback'
  | 'leather'
  | 'loose_leaf'
  | 'other'
  | 'paperback'
  | 'spiral_bound';

export type SerializedBookstoreProduct = {
  'id': string;
  'ean': number | null;
  'asin': string | null;
  'title': string;
  'author': string;
  'summary': string;
  'cover_url': string | null;
  'thumbnail_url': string | null;
  'language_code': string;
  'publishing_date': string | null;
  'word_count': number | null;
  'format': BookstoreProductFormat;
  'is_drm_protected': boolean;
  'is_fulfillable': boolean;
};

export enum FeedPostType {
  BookStart = 'book_start',
  ReadingSession = 'reading_session',
  BookReview = 'book_review',
  Html = 'html',
}

export type BookStart = {
  id: number;
  bookwise_profile: BookwiseUser;
  tracked_book: TrackedBook;
  started_at: number;
  user_description?: string;
  image?: string;
  created: number;
  updated: number;
  feed_post_id?: number;
};

export type BookReview = {
  id: number;
  bookwise_profile: BookwiseUser;
  tracked_book: TrackedBook;
  user_description?: string;
  image?: string;
  rating?: number;
  created: number;
  updated: number;
  feed_post_id?: number;
};

export type HTMLFeedItemData = {
  id: number;
  slug: string;
  html_content: string;
  created: number;
  updated: number;
};

export type FeedPost = {
  id: number;
  post_type: FeedPostType;
  bookwise_profile: BookwiseUser;
  created: number;
  updated: number;
  reading_session: ReadingSession | null;
  book_start: BookStart | null;
  book_review: BookReview | null;
  html_feed_item: HTMLFeedItemData | null;
  feedbacks: Feedback[];
  tracked_book: TrackedBook | null;
  is_global?: boolean | null;
};

export type ReadingSessionFeedPost = Omit<FeedPost, 'post_type' | 'reading_session' | 'book_start' | 'book_review' | 'html_feed_item'> & {
  post_type: FeedPostType.ReadingSession;
  reading_session: ReadingSession;
  book_start: null;
  book_review: null;
  html_feed_item: null;
};

export type BookStartFeedPost = Omit<FeedPost, 'post_type' | 'reading_session' | 'book_start' | 'book_review' | 'html_feed_item'> & {
  post_type: FeedPostType.BookStart;
  reading_session: null;
  book_start: BookStart;
  book_review: null;
  html_feed_item: null;
};

export type BookReviewFeedPost = Omit<FeedPost, 'post_type' | 'reading_session' | 'book_start' | 'book_review' | 'html_feed_item'> & {
  post_type: FeedPostType.BookReview;
  reading_session: null;
  book_start: null;
  book_review: BookReview;
  html_feed_item: null;
};

export type HTMLFeedPost = Omit<FeedPost, 'post_type' | 'reading_session' | 'book_start' | 'book_review' | 'html_feed_item'> & {
  post_type: FeedPostType.Html;
  reading_session: null;
  book_start: null;
  book_review: null;
  html_feed_item: HTMLFeedItemData;
};

export type FeedPostIdGroup = {
  primaryFeedPostId: FeedPost['id'];
  extraPostIds: FeedPost['id'][];
};

export type FeedPostGroup = {
  primaryFeedPost: FeedPost;
  extraPosts: FeedPost[];
};

export type ReadingSessionFeedPostGroup = {
  primaryFeedPost: ReadingSessionFeedPost;
  extraPosts: ReadingSessionFeedPost[];
};

export type BookStartFeedPostGroup = {
  primaryFeedPost: BookStartFeedPost;
  extraPosts: [];
};

export type BookReviewFeedPostGroup = {
  primaryFeedPost: BookReviewFeedPost;
  extraPosts: [];
};

export type HTMLFeedPostGroup = {
  primaryFeedPost: HTMLFeedPost;
  extraPosts: [];
};

export type SupplementalHighlight = {
  id: number;
  text: string;
  location: number;
  location_type: string;
};
